import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import ConnString from './ConnString.js';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';

const Connstring2 = ConnString.connstring;
const passwordPolicyHint = 'Use at least 12 characters with uppercase, lowercase, number, and special character.';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState('info');
  const [message, setMessage] = useState('Enter your new password to complete the reset.');
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Missing reset token. Please request a new password reset link.');
        setTokenValid(false);
        return;
      }

      try {
        const response = await fetch(`${Connstring2}/api/reset-password/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ token }),
        });

        const data = await response.json();
        if (response.ok && data.success) {
          setTokenValid(true);
          setStatus('info');
          setMessage('Token verified. Set your new password.');
          return;
        }

        setTokenValid(false);
        setStatus('error');
        setMessage(data?.message || 'This reset link is invalid or expired.');
      } catch (err) {
        console.error('Token validation failed:', err);
        setTokenValid(false);
        setStatus('error');
        setMessage('Unable to validate reset link right now. Please try again.');
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!tokenValid) {
      setStatus('error');
      setMessage('This reset link is invalid or expired. Please request a new one.');
      return;
    }

    const strongPasswordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{12,}$/;
    if (!strongPasswordPattern.test(newPassword)) {
      setStatus('error');
      setMessage(`Password does not meet complexity requirements. ${passwordPolicyHint}`);
      return;
    }

    if (newPassword !== confirmPassword) {
      setStatus('error');
      setMessage('Passwords do not match.');
      return;
    }

    setLoading(true);
    setStatus('info');
    setMessage('Updating your password...');

    try {
      const response = await fetch(`${Connstring2}/api/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token, newPassword }),
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setStatus('success');
        setMessage(data.message || 'Password updated successfully. Redirecting to login...');
        setTimeout(() => navigate('/'), 1500);
      } else {
        setStatus('error');
        setMessage(data?.message || 'Unable to reset password. Please request a new link.');
      }
    } catch (err) {
      console.error('Reset password request failed:', err);
      setStatus('error');
      setMessage('An error occurred while resetting your password. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={styles.pageWrapper}>
      <Paper elevation={16} sx={styles.formPanel}>
        <Box sx={styles.formHeader}>
          <Typography variant="h5" fontWeight={700}>
            Create a new password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {passwordPolicyHint}
          </Typography>
        </Box>

        {message && (
          <Alert severity={status === 'error' ? 'error' : status} sx={{ mb: 3 }}>
            {message}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={styles.form} noValidate>
          <TextField
            label="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            type="password"
            required
            fullWidth
            autoComplete="new-password"
            disabled={!tokenValid || loading}
            sx={{ backgroundColor: '#f9fbff' }}
          />
          <TextField
            label="Confirm password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            type="password"
            required
            fullWidth
            autoComplete="new-password"
            disabled={!tokenValid || loading}
            sx={{ backgroundColor: '#f9fbff' }}
          />
          <Button type="submit" variant="contained" size="large" fullWidth disabled={loading || !tokenValid}>
            {loading ? 'Updating...' : 'Update password'}
          </Button>
        </Box>

        <Box sx={styles.footerRow}>
          <Link component={RouterLink} to="/" underline="hover">
            Back to sign in
          </Link>
        </Box>
      </Paper>

      <Backdrop sx={styles.backdrop} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

const styles = {
  pageWrapper: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    px: 3,
    py: 6,
    background: 'linear-gradient(135deg, #1f3d7a 0%, #0b2049 100%)',
  },
  formPanel: {
    width: '100%',
    maxWidth: 480,
    borderRadius: 4,
    p: 4,
    backgroundColor: '#f8fbff',
    boxShadow: '0 30px 60px rgba(15,23,42,0.16)',
  },
  formHeader: {
    mb: 3,
  },
  form: {
    display: 'grid',
    gap: 20,
  },
  footerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mt: 3,
    flexWrap: 'wrap',
    gap: 2,
  },
  backdrop: {
    color: '#fff',
    zIndex: (theme) => theme.zIndex.drawer + 1,
  },
};

export default ResetPassword;
